import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { randomUUID } from "node:crypto";

import {
  CliError,
  EXIT_INPUT,
  EXIT_NEEDS_AGENT,
  EXIT_OK,
  EXIT_RUNTIME,
  EXIT_USAGE,
  type CommandOutput,
  directDir,
  exists,
  readJson,
  readText,
  scriptJsonPath,
  sha256Text,
  writeJson,
} from "../../common.js";
import { applyPatchOperations } from "../../domain/script/patch/apply.js";
import { blankScript } from "../../domain/script/lookups.js";
import { collectAssetRefs, collectStateRefs } from "../../domain/script/refs.js";
import {
  expandSelectorToActionRefs,
  isDangerousRegex,
  MAX_GREP_PATTERN_LEN,
  parseInSelector,
  selectorSingleActionPoint,
  type InSelector,
} from "../../domain/script/selector.js";
import {
  parseAnyAddress,
  parseActionRef,
  parseAssetTarget,
  parseSceneRef,
  parseStateTarget,
} from "../../domain/script/address.js";
import { validateScriptV3 } from "../../domain/ingest/validation.js";
import {
  scriptVersionOf,
  validateScriptDocument,
  type ScriptVersion,
} from "../../domain/script/dispatch.js";
import { PATCH_OP_SCHEMA } from "../../domain/script/schema.js";
import { remoteScriptOutputStoreFromEnv } from "../../infra/script-output-api.js";
import {
  assertScriptUnchanged,
  atomicWriteJson,
  semanticScriptHash,
} from "../../infra/script-working-copy.js";
import { LocalScriptOutputStore } from "../../infra/local-script-output-store.js";
import {
  ScriptOutputApiError,
  resolveOutputModeOrThrow,
  type ConfigMissingSlot,
  type ScriptOutputStore,
} from "../../infra/script-output-store.js";
import { summarizeIssues } from "../../domain/script/text-util.js";
import { sortDeep } from "../../domain/script/text-util.js";
import {
  asList,
  type Dict,
  isDict,
  isList,
  strOf,
} from "../../domain/script/shared.js";

export function parseBound(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) {
    throw new CliError("--min-chars / --max-chars must be a non-negative integer.", {
            exitCode: EXIT_USAGE,
            errorCode: "INSPECT_BOUND_INVALID",
            hint: "Pass --min-chars 1000 (positive integer).",
            data: { expected: ["non-negative integer"], actual: [strOf(v)] },
          });
  }
  return Math.max(0, Math.floor(n));
}

interface EpisodeCharCounts {
  total: number;
  dialogue: number;
  action: number;
}

// Count characters in an episode's actions. Splits dialogue-class (`dialogue`,
// `inner_thought`) from narrative/screen-text (`action`) so agents can answer
// "which episodes exceed N spoken chars" vs. raw size. Overlap dialogue stores
// content on `lines[].content`; non-overlap stores it on `content`.
export function episodeCharCounts(scenes: Dict[]): EpisodeCharCounts {
  let dialogue = 0;
  let action = 0;
  for (const scene of scenes) {
    for (const a of asList<Dict>(scene["actions"])) {
      const kind = strOf(a["type"]).trim();
      let chars = strOf(a["content"]).length;
      for (const line of asList<Dict>(a["lines"])) {
        chars += strOf(line["content"]).length;
      }
      if (kind === "dialogue" || kind === "inner_thought") dialogue += chars;
      else action += chars;
    }
  }
  return { total: dialogue + action, dialogue, action };
}

// ---------------------------------------------------------------------------
// ScriptEditSession
// ---------------------------------------------------------------------------

export class ScriptEditSession {
  workspace: string;
  script: Dict;
  readonly version: ScriptVersion;
  // Deep snapshot of the script as loaded, taken BEFORE any edit mutates
  // `script` in place. It is the baseline for the RFC6902 diff a V2 store sends
  // on save (patchScript). V1/local stores ignore it and replace the whole
  // document, so the snapshot is harmless overhead there.
  readonly baseline: Dict;
  readonly baselineHash: string;
  scriptPath: string | null;
  client: ScriptOutputStore | null;
  projectGroupNo: string | null;
  revision: number | null;

  constructor(opts: {
    workspace: string;
    script: Dict;
    scriptPath?: string | null;
    client?: ScriptOutputStore | null;
    projectGroupNo?: string | null;
    revision?: number | null;
  }) {
    this.workspace = opts.workspace;
    this.script = opts.script;
    this.version = scriptVersionOf(opts.script);
    this.baseline = structuredClone(opts.script);
    this.baselineHash = semanticScriptHash(opts.script);
    this.scriptPath = opts.scriptPath ?? null;
    this.client = opts.client ?? null;
    this.projectGroupNo = opts.projectGroupNo ?? null;
    this.revision = opts.revision ?? null;
  }

  get remote(): boolean {
    return this.client !== null;
  }

  get artifactLabel(): string {
    if (this.remote) {
      // Public label — strips the internal `db:/script-output/project-groups/`
      // storage prefix (which leaks implementation), but keeps the project id
      // and revision so two different project groups at the same revision stay
      // distinguishable in user-visible report.artifacts entries. The project
      // id is not a secret: the user supplies it themselves via
      // `--project-group-no` or env config.
      return `script-output/${this.projectGroupNo}@revision/${this.revision ?? 0}`;
    }
    return this.scriptPath ?? "";
  }
}

/**
 * Validate that the target-selection flags on `opts` are coherent, throwing a
 * CliError on any contradiction. The SINGLE conflict gate for every file-capable
 * entry point (loadScriptForEdit AND commandCreate) — neither may resolve a
 * target without passing through here, so a store flag can never be silently
 * dropped on the local-file branch (the wrong-target write this design forbids).
 *
 * `--remote`/`--local`/`--project-group-no` select a STORE; `--script-path`
 * selects a local FILE. The two are mutually exclusive.
 */
export function assertTargetCoherent(opts: Dict): void {
  const scriptPath = strOf(opts["script_path"]).trim() !== "";
  const remote = Boolean(opts["remote"]);
  const local = Boolean(opts["local"]);
  const projectGroup = strOf(opts["project_group_no"]).trim() !== "";
  const targetFlagConflict = (received: string[]): CliError =>
    new CliError("The selected output-target flags are mutually exclusive.", {
      exitCode: EXIT_USAGE,
      errorCode: "TARGET_FLAG_CONFLICT",
      hint: "Keep a single target selector and drop the others.",
      data: {
        expected: ["one of: --script-path (a local file), or --remote/--local (the store)"],
        actual: received,
      },
    });

  // Most fundamental first, so the message names the real contradiction even
  // when several conflicting flags are passed at once.
  if (remote && local) throw targetFlagConflict(["--remote", "--local"]);
  if ((remote || local) && scriptPath) {
    throw targetFlagConflict([remote ? "--remote" : "--local", "--script-path"]);
  }
  // A project group names a REMOTE target, so it cannot combine with any local
  // target — a local file (--script-path) OR the local store (--local).
  // `--remote --project-group-no` is the one valid pairing and is left alone.
  if (projectGroup && (scriptPath || local)) {
    throw targetFlagConflict(["--project-group-no", scriptPath ? "--script-path" : "--local"]);
  }
}

export function scriptOutputClient(opts: Dict): ScriptOutputStore {
  const mode = resolveOutputModeOrThrow({
    remote: opts["remote"],
    local: opts["local"],
    projectGroupNo: opts["project_group_no"],
  });
  try {
    if (mode === "local") {
      return LocalScriptOutputStore.fromEnv(strOf(opts["workspace_path"]).trim() || undefined);
    }
    return remoteScriptOutputStoreFromEnv(strOf(opts["project_group_no"]).trim() || null);
  } catch (exc) {
    // ScriptOutputApiError carries a `configMissing` discriminator so callers
    // can show the user which slot is unconfigured without naming the env var.
    if (exc instanceof ScriptOutputApiError) {
      const { title, message } = describeConfigMissing(mode, exc.configMissing);
      throw new CliError(message, {
              exitCode: EXIT_INPUT,
              errorCode: "SCRIPT_API_CONFIG_MISSING",
              hint: "Run `scriptctl doctor` to identify missing configuration.",
              data: { expected: [
          mode === "local"
            ? "SCRIPTCTL_OUTPUT_DIR"
            : "AWB_BASE_URL, LINGJING_AWB_ACCESS_KEY, and SANDBOX_PROJECT_GROUP_NO (or --project-group-no)",
        ], actual: [exc.message, `configMissing: ${exc.configMissing ?? "unknown"}`] },
            });
    }
    // Belt-and-suspenders: anything else that escapes RemoteScriptOutputStore.fromEnv
    // (a TypeError on a malformed gateway URL, future programmer errors) still
    // surfaces as a clean CliError envelope rather than the generic
    // the generic runtime-error path in cli.ts.
    const raw = exc instanceof Error ? exc.message : String(exc);
    throw new CliError(mode === "local"
        ? "Local output dir is not configured for this environment."
        : "Gateway is not configured for this environment.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_API_CONFIG_MISSING",
            hint: "Run `scriptctl doctor` to identify missing configuration.",
            data: { expected: [mode === "local" ? "valid local output dir" : "valid gateway configuration"], actual: [raw] },
          });
  }
}

function describeConfigMissing(
  mode: "local" | "remote",
  slot: ConfigMissingSlot | null,
): { title: string; message: string } {
  // Mode wins over slot — if the caller is in local mode, the failure is
  // unambiguously about the output dir even if the underlying error tagged
  // the slot differently.
  // NOTE: this early return short-circuits the exhaustive switch below. New
  // ConfigMissingSlot values for remote-mode callers are guarded by the
  // switch (TS will flag a missing case); values intended for local-mode-only
  // callers must be handled explicitly above this guard.
  if (mode === "local") {
    return {
      title: "SCRIPT API BLOCKED: local output dir missing",
      message:
        "Local output dir is not configured. Set `SCRIPTCTL_OUTPUT_DIR` to a writable directory.",
    };
  }
  // Unknown / unspecified failures in remote mode default to the gateway
  // bucket — keeps the pre-0.7.0 UX stable.
  if (slot === null) {
    return {
      title: "SCRIPT API BLOCKED: gateway not configured",
      message: "Gateway is not configured for this environment.",
    };
  }
  // After the two guards above, `slot` is narrowed to ConfigMissingSlot. TS
  // exhaustiveness on the switch means adding a new slot without handling it
  // here is a compile error — `assertNever` makes the intent explicit.
  switch (slot) {
    case "output":
      return {
        title: "SCRIPT API BLOCKED: local output dir missing",
        message:
          "Local output dir is not configured. Set `SCRIPTCTL_OUTPUT_DIR` to a writable directory.",
      };
    case "project":
      return {
        title: "SCRIPT API BLOCKED: project identifier missing",
        message:
          "Project identifier is not configured. Pass `--project-group-no <id>` or configure the project for this environment.",
      };
    case "access":
      return {
        title: "SCRIPT API BLOCKED: access credential missing",
        message: "Access credential is not configured for this environment.",
      };
    case "gateway":
      return {
        title: "SCRIPT API BLOCKED: gateway not configured",
        message: "Gateway is not configured for this environment.",
      };
    default:
      return assertNever(slot);
  }
}

function assertNever(x: never): never {
  throw new Error(`describeConfigMissing: unhandled ConfigMissingSlot ${JSON.stringify(x)}`);
}

export function apiErrorToCli(title: string, exc: ScriptOutputApiError): CliError {
  const rawMessage = exc.message || "script-output API failed";
  // Use the gateway's own canonical Chinese keywords as the only signal. Free
  // text like "revision" or "not found" can leak in from upstream proxies,
  // DNS errors ("name not found"), or HTTP 404 routing mismatches — matching on
  // those substrings would misroute auth/network failures into the conflict /
  // missing-script UX. The current CAS endpoint uses code 9002; the Chinese
  // keyword keeps the retained legacy endpoint compatible.
  const isConflict = String(exc.code) === "9002" || rawMessage.includes("版本冲突");
  const isMissing = rawMessage.includes("剧本不存在");
  const publicMessage = isConflict
    ? "Script has been updated by another revision; keep local work and merge before retrying."
    : isMissing
      ? "Script not found on the gateway."
      : "Gateway request failed.";
  return new CliError(publicMessage, {
           exitCode: exc.status !== null && exc.status >= 500 ? EXIT_RUNTIME : EXIT_INPUT,
           errorCode: isConflict
      ? "REVISION_CONFLICT"
      : isMissing
        ? "SCRIPT_NOT_FOUND"
        : "SCRIPT_API_FAILED",
           hint: isConflict
        ? "Keep the local file, inspect the latest remote snapshot, and merge before publishing again."
        : isMissing
          ? "Run `scriptctl publish` to publish a script first."
          : "Run `scriptctl doctor` to verify gateway configuration; retry if it was transient.",
           data: { expected: ["successful script-output gateway request"], actual: [rawMessage, `status: ${exc.status ?? "<unknown>"}`] },
         });
}

export async function currentRevisionOrZero(client: ScriptOutputStore): Promise<number> {
  try {
    const revision = await client.getRevision();
    if (!revision) return 0;
    return Number(revision["revision"] ?? 0);
  } catch (exc) {
    if (exc instanceof ScriptOutputApiError) {
      throw apiErrorToCli("SCRIPT API BLOCKED: Revision query failed", exc);
    }
    throw exc;
  }
}

export async function loadRemoteScript(opts: Dict, workspace: string): Promise<ScriptEditSession> {
  const client = scriptOutputClient(opts);
  let script: Record<string, unknown> | null;
  let revision: Record<string, unknown> | null;
  try {
    script = await client.getScript();
    revision = await client.getRevision();
  } catch (exc) {
    if (exc instanceof ScriptOutputApiError) {
      throw apiErrorToCli("SCRIPT API BLOCKED: Script query failed", exc);
    }
    throw exc;
  }
  if (script === null) {
    throw new CliError("No script has been published for this project.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_NOT_FOUND",
            hint: "Run `scriptctl publish` first, or pass `--script-path` to edit a local script JSON.",
            data: { expected: ["existing script-output project document"], actual: [`projectGroupNo=${client.projectGroupNo}`] },
          });
  }
  if (!isDict(script)) {
    throw new CliError("script root invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "SCRIPT_ROOT_INVALID",
            hint: "Replace the final script with a valid script document object.",
            data: { expected: ["script root object"], actual: [typeof script] },
          });
  }
  return new ScriptEditSession({
    workspace,
    script,
    client,
    projectGroupNo: client.projectGroupNo,
    revision: Number((revision ?? {})["revision"] ?? 0),
  });
}

export async function loadScriptForEdit(opts: Dict): Promise<ScriptEditSession> {
  const workspace = strOf(opts["workspace_path"] || "workspace");
  const p = scriptJsonPath(opts);
  if (!exists(p)) {
    throw new CliError("Local script file not found.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_NOT_FOUND",
            hint: "Run `scriptctl checkout`, `scriptctl create`, or pass --script-path to an existing script.",
            data: { expected: ["existing local script JSON"], actual: [p] },
          });
  }
  let data: unknown;
  try {
    data = readJson(p);
  } catch (exc) {
    throw new CliError("script JSON invalid.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_JSON_INVALID",
            hint: "Fix the local script JSON syntax before editing.",
            data: { expected: ["valid JSON"], actual: [`${p}: ${(exc as Error).message}`] },
          });
  }
  if (!isDict(data)) {
    throw new CliError("script root invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "SCRIPT_ROOT_INVALID",
            hint: "Use a valid script document object.",
            data: { expected: ["script root object"], actual: [Array.isArray(data) ? "array" : typeof data] },
          });
  }
  return new ScriptEditSession({ workspace, scriptPath: p, script: data });
}

// Validate the IN-MEMORY script as Script v3. On a dry-run the edits live only
// in session.script (never written), so validating the object (not the on-disk
// file) is what makes "would pass" honest. requireSource is accepted for call-
// site compatibility but ignored — v3 scripts carry no source-coverage contract.
export function validateSession(session: ScriptEditSession, _opts: { requireSource?: boolean } = {}): Dict {
  const report = validateScriptReport(session.script);
  if (session.remote) report["script_path"] = session.artifactLabel;
  return report;
}

function validationResultReport(
  script: Dict,
  validation: ReturnType<typeof validateScriptDocument>,
): Dict {
  const { ok, errors, warnings } = validation;
  const issues: Dict[] = [
    ...errors.map((e) => ({
      severity: "error",
      code: e.code,
      path: e.path,
      summary: e.message,
      ...(e.repair_hint ? { repair_hint: e.repair_hint } : {}),
    })),
    ...warnings.map((w) => ({
      severity: "warning",
      code: w.code,
      path: w.path,
      summary: w.message,
      ...(w.repair_hint ? { repair_hint: w.repair_hint } : {}),
    })),
  ];
  const episodes = asList<Dict>(script["episodes"]);
  let scenes = 0;
  let actions = 0;
  for (const ep of episodes) {
    for (const sc of asList<Dict>(ep["scenes"])) {
      scenes += 1;
      actions += asList<Dict>(sc["actions"]).length;
    }
  }
  return {
    passed: ok,
    has_blocking: !ok,
    issues,
    stats: {
      actors: asList<Dict>(script["actors"]).length,
      locations: asList<Dict>(script["locations"]).length,
      props: asList<Dict>(script["props"]).length,
      episodes: episodes.length,
      scenes,
      actions,
    },
  };
}

// Adapt a versioned validator's {ok, errors, warnings} into the report shape
// consumed by CLI output and gateway validation sync.
export function validateScriptReport(script: Dict): Dict {
  return validationResultReport(script, validateScriptDocument(script));
}

export function validateScriptV3Report(script: Dict): Dict {
  return validationResultReport(script, validateScriptV3(script));
}

function validationIssueKey(issue: Dict): string {
  return JSON.stringify([
    strOf(issue["code"]),
    strOf(issue["path"]),
    strOf(issue["summary"]),
  ]);
}

export function mutationValidationWarnings(
  session: ScriptEditSession,
  validation: Dict,
): Dict[] {
  const baseline = validateScriptReport(session.baseline);
  const baselineCounts = new Map<string, number>();
  for (const issue of asList<Dict>(baseline["issues"])) {
    const key = validationIssueKey(issue);
    baselineCounts.set(key, (baselineCounts.get(key) ?? 0) + 1);
  }
  const warnings: Dict[] = [];
  for (const issue of asList<Dict>(validation["issues"])) {
    const key = validationIssueKey(issue);
    const remaining = baselineCounts.get(key) ?? 0;
    if (remaining > 0) {
      baselineCounts.set(key, remaining - 1);
      continue;
    }
    warnings.push({
      ...issue,
      validation_severity: issue["severity"],
      severity: "warning",
    });
  }
  return warnings;
}

function validationIssuePath(issue: Dict): string | null {
  if (issue["path"]) return strOf(issue["path"]);
  const parts: string[] = [];
  if (issue["episode"] !== null && issue["episode"] !== undefined) parts.push(strOf(issue["episode"]));
  if (issue["scene"] !== null && issue["scene"] !== undefined) parts.push(strOf(issue["scene"]));
  if (issue["action_index"] !== null && issue["action_index"] !== undefined) parts.push(`action[${issue["action_index"]}]`);
  return parts.length > 0 ? parts.join("/") : null;
}

function validationIssueSeverity(raw: unknown): string {
  const severity = strOf(raw || "info");
  if (severity === "blocking" || severity === "error") return "error";
  if (severity === "warning") return "warning";
  return "info";
}

function validationToApiPayload(validation: Dict): [string, Dict, Dict[]] {
  const issues = asList<Dict>(validation["issues"]);
  const status = validation["passed"] ? "valid" : "invalid";
  const summary: Dict = {
    passed: Boolean(validation["passed"]),
    hasBlocking: Boolean(validation["has_blocking"]),
    stats: validation["stats"] ?? {},
    issueCount: issues.length,
  };
  const apiIssues: Dict[] = [];
  for (const issue of issues) {
    if (!isDict(issue)) continue;
    const payload: Dict = { ...issue };
    for (const key of ["severity", "code", "path", "summary", "repair_hint"]) delete payload[key];
    apiIssues.push({
      severity: validationIssueSeverity(issue["severity"]),
      code: strOf(issue["code"] || "SCRIPT_VALIDATION_ISSUE"),
      path: validationIssuePath(issue),
      summary: strOf(issue["summary"]),
      repairHint: issue["repair_hint"],
      payload,
    });
  }
  return [status, summary, apiIssues];
}

export async function syncValidationResult(session: ScriptEditSession, validation: Dict, revision?: number | null): Promise<void> {
  if (!session.remote || session.client === null) return;
  const targetRevision = Number(revision ?? session.revision ?? 0);
  if (targetRevision <= 0) return;
  const [status, summary, issues] = validationToApiPayload(validation);
  try {
    await session.client.saveValidationResult({
      revision: targetRevision,
      validationStatus: status,
      validationSummary: summary,
      issues,
    });
  } catch (exc) {
    if (exc instanceof ScriptOutputApiError) {
      throw apiErrorToCli("SCRIPT API BLOCKED: Validation sync failed", exc);
    }
    throw exc;
  }
}

// Idempotency key for gateway writes. Returns the agent-provided --request-id
// when present, otherwise derives a deterministic key from (op, post-mutation
// script content). baseRevision is intentionally NOT in the hash: in a
// partial-success retry the client reloads at a new baseRevision but the
// post-mutation script is identical, so the hash matches and the gateway can
// dedupe. The cost of including baseRevision (an old version of this code)
// was that retries produced different ids and committed redundant no-op
// revisions to the gateway history.
export function requestIdForScriptWrite(
  opts: Dict,
  op: string,
  payload: { script: Dict },
): string {
  const explicit = strOf(opts["request_id"]).trim();
  if (explicit) return explicit;
  const canonical = JSON.stringify(sortDeep({
    op,
    script: payload.script,
  }));
  return `scriptctl:${op}:${sha256Text(canonical)}`;
}

export async function saveScriptSession(session: ScriptEditSession, _opts: Dict, _op: string): Promise<[number | null, boolean]> {
  if (session.scriptPath === null) throw new Error("local script session missing script path");
  assertScriptUnchanged(session.scriptPath, session.baselineHash);
  atomicWriteJson(session.scriptPath, session.script);
  return [null, false];
}

export function scriptSourceText(workspace: string): string {
  const sourcePath = path.join(workspace, "source.txt");
  return exists(sourcePath) ? readText(sourcePath) : "";
}

export function patchOperationsFromPayload(payload: unknown): Dict[] {
  let operations: unknown[];
  if (isList(payload)) {
    operations = payload;
  } else if (isDict(payload) && isList(payload["ops"])) {
    operations = payload["ops"];
  } else if (isDict(payload) && isList(payload["operations"])) {
    operations = payload["operations"];
  } else if (isDict(payload)) {
    operations = [payload];
  } else {
    throw new CliError("Patch schema invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "PATCH_BLOCKED_PATCH_SCHEMA_INVALID",
            hint: "Fix patch JSON and rerun patch.",
            data: { expected: ["object, array, object with ops[], or object with operations[]"], actual: [Array.isArray(payload) ? "array" : typeof payload] },
          });
  }
  if (operations.some((op) => !isDict(op))) {
    throw new CliError("Patch operation invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "PATCH_BLOCKED_PATCH_OPERATION_INVALID",
            hint: "Fix patch JSON and rerun patch.",
            data: { expected: ["each operation must be an object"], actual: [JSON.stringify(operations).slice(0, 500)] },
          });
  }
  return operations as Dict[];
}

// ---------------------------------------------------------------------------
// commandScriptValidate / commandScriptInspect
// ---------------------------------------------------------------------------

// ===========================================================================
// do — verb-script transaction (the agent-friendly alternative to JSON patch)
// ===========================================================================

// Minimal shell-like tokenizer: whitespace-split honoring "..."/'...' quoting
// and backslash escapes. Enough to carry screenplay content with spaces/quotes.
